/**
 * From pasted columns to a model: choose the rows that have every needed value, and turn
 * each chosen predictor into numbers.
 *
 * A numeric column is used as it is. A text column with exactly two levels becomes a 0/1
 * dummy: the level R would call the "success" level (the later one alphabetically; the
 * larger, for numbers) is coded 1, the other is the reference. Text columns with more
 * levels cannot be used directly — code them as 0/1 columns first — and are not offered.
 */

import {
  completeRows,
  type DataColumn,
  defaultPositiveLevel,
  encodeColumn,
  take,
} from "@/lib/tool-kit/stats-regression/dataset";

export interface PredictorCoding {
  /** 1 level of a two-level text column; null for a numeric column. */
  positive: string | null;
  reference: string | null;
}

export interface Design {
  /** Row indexes (into the pasted data) that have every value. */
  rows: number[];
  dropped: number;
  y: number[];
  predictors: number[][];
  codings: PredictorCoding[];
}

export function codingOf(column: DataColumn): PredictorCoding {
  if (column.isNumeric) {
    return { positive: null, reference: null };
  }
  const positive = defaultPositiveLevel(column);
  return {
    positive,
    reference: column.levels.find((level) => level !== positive) ?? null,
  };
}

/**
 * The complete cases for a response and predictors. Returns null when a chosen column
 * cannot be modelled (more than two text levels) — callers only offer modellable ones.
 */
export function buildDesign(
  response: readonly (number | null)[],
  predictors: readonly DataColumn[]
): Design | null {
  const columns: (number | null)[][] = [];
  const codings: PredictorCoding[] = [];
  for (const column of predictors) {
    const encoded = encodeColumn(column);
    if (encoded === null) {
      return null;
    }
    columns.push(encoded);
    codings.push(codingOf(column));
  }
  const { rows, dropped } = completeRows([response, ...columns]);
  return {
    rows,
    dropped,
    y: take(response, rows),
    predictors: columns.map((column) => take(column, rows)),
    codings,
  };
}
